import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, KeyRound, ShieldCheck } from "lucide-react";

export const Route = createFileRoute("/reset-password")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Choose a new password · TruWe" },
      { name: "description", content: "Set a new password for your TruWe account." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [valid, setValid] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  // The recovery link puts a session in the URL hash; Supabase picks it up and
  // fires PASSWORD_RECOVERY / SIGNED_IN. Wait for that before showing the form.
  useEffect(() => {
    let done = false;
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" || (session && !done)) {
        done = true;
        setValid(true);
        setReady(true);
      }
    });
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        done = true;
        setValid(true);
      }
      setReady(true);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Use at least 8 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      toast.success("Password updated. You're signed in.");
      navigate({ to: "/account", replace: true });
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Couldn't update your password");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-4 py-12">
      <h1 className="flex items-center gap-2 font-display text-3xl font-semibold text-brand-deep">
        <KeyRound size={24} className="text-brand" /> New password
      </h1>

      {!ready ? (
        <div className="mt-6 h-28 animate-pulse rounded-2xl bg-muted/60" />
      ) : valid ? (
        <>
          <p className="mt-2 text-sm text-muted-foreground">
            Choose a new password for your TruWe account. Minimum 8 characters.
          </p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="new-password">New password</Label>
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                autoFocus
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-11"
              />
            </div>
            <div>
              <Label htmlFor="confirm-password">Confirm password</Label>
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="h-11"
              />
            </div>
            <Button type="submit" className="h-11 w-full" disabled={busy}>
              {busy && <Loader2 size={15} className="mr-1.5 animate-spin" />}
              Update password
            </Button>
          </form>
          <p className="mt-4 flex items-start gap-1.5 text-xs text-muted-foreground">
            <ShieldCheck size={13} className="mt-0.5 shrink-0 text-brand" />
            After updating, you'll stay signed in on this device.
          </p>
        </>
      ) : (
        <>
          <p className="mt-2 text-sm text-muted-foreground">
            This reset link is invalid or has expired. Request a fresh one from the sign-in page.
          </p>
          <Button asChild className="mt-6 h-11 w-full">
            <Link to="/auth">Back to sign in</Link>
          </Button>
        </>
      )}

      <Link to="/" className="mt-6 text-center text-xs text-muted-foreground hover:text-brand">
        ← Back to store
      </Link>
    </main>
  );
}
